import { Bookmark, ObjectGroup } from "@novorender/data-js-api";
import type { FetchBaseQueryError, QueryReturnValue } from "@reduxjs/toolkit/query";
import { createApi } from "@reduxjs/toolkit/query/react";
import { minutesToSeconds } from "date-fns";

import { type ArcgisWidgetConfig } from "features/arcgis/types";
import { networkFirst, offlineHostData } from "features/offline/hostData/offlineHostData";
import { BookmarkAccess, BookmarkCatalogEntry, ScopedCatalogEntry } from "types/bookmark";
import { CustomProperties } from "types/project";

import { AuthScope, ModelTreePermission, ScopeRoleAssignment } from "./authTypes";
import { DeviationProjectConfig } from "./deviationTypes";
import { Omega365Configuration, Omega365DynamicDocument, Omega365View } from "./omega365Types";
import { grantedPermissions, Permission } from "./permissions";
import { BuildProgressResult, EpsgSearchResult, ProjectInfo, ProjectVersion } from "./projectTypes";
import { getDataV2DynamicBaseQuery, isOnline } from "./utils";

export const dataV2Api = createApi({
    reducerPath: "dataV2",
    baseQuery: getDataV2DynamicBaseQuery(),
    tagTypes: [
        "PropertyTreeFavorites",
        "ProjectProgress",
        "Deviation",
        "Bookmarks",
        "BookmarkState",
        "Groups",
        "OmegaItems",
    ],
    endpoints: (builder) => ({
        getOmega365ProjectConfig: builder.query<Omega365Configuration, { projectId: string }>({
            query: ({ projectId }) => `/explorer/${projectId}/omega365/configuration`,
        }),
        setOmega365ProjectConfig: builder.mutation<void, { projectId: string; config: Omega365Configuration }>({
            query: ({ projectId, config }) => ({
                url: `/explorer/${projectId}/omega365/configuration`,
                method: "PUT",
                body: config,
            }),
        }),
        previewOmega365ProjectViewConfig: builder.query<
            Omega365DynamicDocument[],
            { projectId: string; objectId: number; baseURL: string; view: Omega365View }
        >({
            query: ({ projectId, objectId, baseURL, view }) => ({
                url: `/explorer/${projectId}/omega365/configuration/preview-view`,
                method: "POST",
                body: { objectId, baseURL, view },
            }),
        }),
        getOmega365ViewDocumentLinks: builder.query<
            Omega365DynamicDocument[],
            { projectId: string; objectId: number; viewId: string }
        >({
            query: ({ projectId, objectId, viewId }) =>
                `/explorer/${projectId}/omega365/views/${viewId}/documents/${objectId}`,
            providesTags: ["OmegaItems"],
        }),
        getOmega365AllViewDocumentLinks: builder.query<
            { ready: boolean; documents: Omega365DynamicDocument[] },
            { projectId: string; viewId: string }
        >({
            query: ({ projectId, viewId }) => `/explorer/${projectId}/omega365/views/${viewId}/documents`,
        }),
        getOmegaActivityTypes: builder.query<{ id: number; name: string }[], { projectId: string }>({
            query: ({ projectId }) => `/explorer/${projectId}/omega365/activity-types`,
            keepUnusedDataFor: 60 * 60,
            transformResponse: idAndNameUniqueById,
        }),
        getOmegaOrgUnits: builder.query<{ id: number; name: string }[], { projectId: string }>({
            query: ({ projectId }) => `/explorer/${projectId}/omega365/org-units`,
            keepUnusedDataFor: 60 * 60,
            transformResponse: idAndNameUniqueById,
        }),
        getOmegaObjectId: builder.query<{ id: number } | undefined, { projectId: string; objectName: string }>({
            query: ({ projectId, objectName }) => ({
                url: `/explorer/${projectId}/omega365/find-object`,
                params: { objectName },
            }),
            keepUnusedDataFor: 60 * 60,
        }),
        createOmegaActivity: builder.mutation<
            { newActivityUrl: string },
            {
                projectId: string;
                activity: { name: string; objectId: number; activityTypeId: number; orgUnitId: number };
            }
        >({
            query: ({ projectId, activity }) => ({
                url: `/explorer/${projectId}/omega365/activity`,
                method: "POST",
                body: activity,
            }),
            invalidatesTags: ["OmegaItems"],
        }),
        getPropertyTreeFavorites: builder.query<string[], { projectId: string }>({
            query: ({ projectId }) => `/explorer/${projectId}/propertytree/favorites`,
            keepUnusedDataFor: minutesToSeconds(10),
            providesTags: ["PropertyTreeFavorites"],
            transformResponse: (data: { propertyName: string }[]) => data.map(({ propertyName }) => propertyName),
        }),
        setPropertyTreeFavorites: builder.mutation<void, { favorites: string[]; projectId: string }>({
            query: ({ projectId, favorites }) => ({
                url: `/explorer/${projectId}/propertytree/favorites`,
                method: "PUT",
                body: favorites.map((propertyName) => ({ propertyName })),
            }),
            invalidatesTags: ["PropertyTreeFavorites"],
            async onQueryStarted({ favorites, projectId }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    dataV2Api.util.updateQueryData("getPropertyTreeFavorites", { projectId }, () => favorites),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        getProject: builder.query<ProjectInfo, { projectId: string }>({
            query: ({ projectId }) => `/projects/${projectId}`,
        }),
        getProjectLatestVersion: builder.query<ProjectVersion | undefined, { projectId: string }>({
            query: ({ projectId }) => `/projects/${projectId}/versions/latest`,
        }),
        getModelTreePermissions: builder.query<ModelTreePermission[], { projectId: string }>({
            query: ({ projectId }) => `/explorer/${projectId}/modeltree/permissions`,
        }),
        getArcgisWidgetConfig: builder.query<ArcgisWidgetConfig, { projectId: string }>({
            query: ({ projectId }) => `/explorer/${projectId}/arcgis/config`,
        }),
        putArcgisWidgetConfig: builder.mutation<object, { projectId: string; config: ArcgisWidgetConfig }>({
            query: ({ projectId, config }) => ({
                url: `/explorer/${projectId}/arcgis/config`,
                method: "PUT",
                body: config,
            }),
        }),
        // `null` when none is saved: the server answers with an empty body.
        getDeviationProfiles: builder.query<DeviationProjectConfig | null, { projectId: string }>({
            queryFn: ({ projectId }, api, _extraOptions, baseQuery) =>
                networkFirst({
                    isOnline: isOnline(api),
                    network: () => typed<DeviationProjectConfig | null>(baseQuery(`/explorer/${projectId}/deviations`)),
                    offline: () => offlineHostData.deviationProfiles(projectId),
                }),
            providesTags: (_result, _error, { projectId }) => [{ type: "Deviation", id: projectId }],
        }),
        setDeviationProfiles: builder.mutation<void, { projectId: string; config: DeviationProjectConfig }>({
            query: ({ projectId, config }) => ({
                url: `/explorer/${projectId}/deviations`,
                method: "PUT",
                body: config,
            }),
            invalidatesTags: (_result, _error, { projectId }) => [{ type: "Deviation", id: projectId }],
        }),
        calcDeviations: builder.mutation<void, { projectId: string; config: DeviationProjectConfig }>({
            query: ({ projectId, config }) => ({
                url: `/projects/${projectId}/calcdeviations`,
                method: "PATCH",
                body: config,
            }),
            invalidatesTags: (_result, _error, { projectId }) => [{ type: "ProjectProgress", id: projectId }],
        }),
        getProjectProgress: builder.query<BuildProgressResult, { projectId: string; position?: number }>({
            query: ({ projectId, position }) => ({
                url: `/projects/${projectId}/progress`,
                params: { position },
            }),
            providesTags: (_result, _error, { projectId }) => [{ type: "ProjectProgress", id: projectId }],
        }),
        getFileDownloadLink: builder.query<
            string,
            { relativeUrl: string } | { projectId: string; fileId: string; version: number }
        >({
            query: (params) => ({
                url:
                    "relativeUrl" in params
                        ? `/${params.relativeUrl}`
                        : `/projects/${params.projectId}/files/${params.fileId}/downloadlink/${params.version}`,
                responseHandler: async (resp) => {
                    const result = await resp.text();
                    return result;
                },
            }),
        }),
        searchEpsg: builder.query<EpsgSearchResult, { query: string }>({
            query: ({ query }) => ({
                url: `/epsg`,
                method: "POST",
                body: query,
            }),
        }),
        getCurrentUserRoleAssignments: builder.query<
            ScopeRoleAssignment[],
            { organizationId: string; projectId: string; viewerSceneId?: string }
        >({
            query: ({ organizationId, projectId, viewerSceneId }) => ({
                url: "/roles/assignments/current-user",
                method: "GET",
                params: { organizationId, projectId, viewerSceneId },
            }),
        }),
        checkPermissions: builder.query<Permission[], { scope: AuthScope; permissions: Permission[] }>({
            query: ({ scope, permissions }) => ({
                url: "/roles/check-permissions",
                method: "POST",
                body: {
                    scope,
                    permissionIds: permissions,
                },
            }),
            transformResponse: (resp: boolean[], _, { permissions }) => grantedPermissions(permissions, resp),
        }),
        getBookmarkCatalog: builder.query<ScopedCatalogEntry[], { projectId: string; personal?: boolean }>({
            queryFn: async ({ projectId, personal }, api, _extraOptions, baseQuery) => {
                const result = await networkFirst({
                    isOnline: isOnline(api),
                    network: () =>
                        typed<BookmarkCatalogEntry[]>(
                            baseQuery(`/explorer/v3/${projectId}/bookmarks/${bookmarkScope(personal)}/catalog`),
                        ),
                    offline: () => offlineHostData.bookmarkCatalog(projectId, bookmarkScope(personal)),
                });
                return result.error
                    ? result
                    : {
                          data: result.data.map((entry) => ({
                              ...entry,
                              access: personal ? BookmarkAccess.Personal : BookmarkAccess.Public,
                          })),
                      };
            },
            providesTags: (_result, _error, arg) => [bookmarksScopeTag(arg)],
            keepUnusedDataFor: minutesToSeconds(10),
        }),
        getBookmarkState: builder.query<Bookmark, BookmarkStateArgs>({
            queryFn: (args, api, _extraOptions, baseQuery) =>
                networkFirst({
                    isOnline: isOnline(api),
                    network: () => typed<Bookmark>(baseQuery(bookmarkStatePath(args))),
                    // As captured with the synced scene: its object ids are that version's.
                    offline: () =>
                        offlineHostData.bookmarkState(args.projectId, bookmarkScope(args.personal), args.bookmarkId),
                }),
            providesTags: (_result, _error, arg) => [{ type: "BookmarkState", id: arg.bookmarkId }],
            keepUnusedDataFor: minutesToSeconds(2),
        }),
        updateBookmarkGroupStatus: builder.mutation<
            void,
            {
                projectId: string;
                updates: { groupId: string; status: "hidden" | "frozen" | "none" }[];
                personal?: boolean;
            }
        >({
            query: ({ projectId, updates, personal }) => ({
                url: `/explorer/v3/${projectId}/bookmarks/${bookmarkScope(personal)}/group-status`,
                method: "POST",
                body: { updates },
            }),
            invalidatesTags: ["BookmarkState"],
        }),
        renameBookmarkCollection: builder.mutation<
            void,
            { projectId: string; from: string; to: string; personal?: boolean }
        >({
            query: ({ projectId, from, to, personal }) => ({
                url: `/explorer/v3/${projectId}/bookmarks/${bookmarkScope(personal)}/collection/rename`,
                method: "POST",
                body: { from, to },
            }),
            invalidatesTags: (_result, _error, arg) => [bookmarksScopeTag(arg)],
        }),
        createBookmark: builder.mutation<
            void,
            {
                projectId: string;
                bookmark: Bookmark;
                projectVersion: string;
                personal?: boolean;
            }
        >({
            query: ({ projectId, bookmark, projectVersion, personal }) => ({
                url: `/explorer/v3/${projectId}/bookmarks/${bookmarkScope(personal)}`,
                method: "POST",
                body: bookmark,
                params: { projectVersion },
            }),
            invalidatesTags: (_result, _error, arg) => [bookmarksScopeTag(arg)],
        }),
        updateBookmark: builder.mutation<
            void,
            {
                projectId: string;
                bookmark: Bookmark;
                projectVersion: string;
                personal?: boolean;
            }
        >({
            query: ({ projectId, bookmark, projectVersion, personal }) => ({
                url: `/explorer/v3/${projectId}/bookmarks/${bookmarkScope(personal)}/${bookmark.id}`,
                method: "PUT",
                body: bookmark,
                params: { projectVersion },
            }),
            invalidatesTags: (_result, _error, arg) => [
                bookmarksScopeTag(arg),
                { type: "BookmarkState", id: arg.bookmark.id },
            ],
        }),
        deleteBookmark: builder.mutation<
            void,
            {
                projectId: string;
                bookmarkId: string;
                projectVersion: string;
                personal?: boolean;
            }
        >({
            query: ({ projectId, bookmarkId, projectVersion, personal }) => ({
                url: `/explorer/v3/${projectId}/bookmarks/${bookmarkScope(personal)}/${bookmarkId}`,
                method: "DELETE",
                params: { projectVersion },
            }),
            invalidatesTags: (_result, _error, arg) => [
                bookmarksScopeTag(arg),
                { type: "BookmarkState", id: arg.bookmarkId },
            ],
        }),
        getSharelinkBookmark: builder.query<Bookmark, { projectId: string; sharelinkId: string }>({
            query: ({ projectId, sharelinkId }) => `/explorer/v3/${projectId}/bookmarks/sharelink/${sharelinkId}`,
        }),
        createSharelink: builder.mutation<void, { projectId: string; bookmark: Bookmark; projectVersion: string }>({
            query: ({ projectId, bookmark, projectVersion }) => ({
                url: `/explorer/v3/${projectId}/bookmarks/sharelink`,
                method: "POST",
                body: bookmark,
                params: { projectVersion },
            }),
        }),
        getGroupIds: builder.query<number[], { projectId: string; groupId: string }>({
            queryFn: ({ projectId, groupId }, api, _extraOptions, baseQuery) =>
                networkFirst({
                    isOnline: isOnline(api),
                    network: () => typed<number[]>(baseQuery(`/explorer/${projectId}/groups/${groupId}/ids`)),
                    offline: () => offlineHostData.groupIds(projectId, groupId),
                }),
            providesTags: ["Groups"],
        }),
        saveGroups: builder.mutation<void, { projectId: string; groups: ObjectGroup[] }>({
            query: ({ projectId, groups }) => ({
                url: `/explorer/${projectId}/scene-data`,
                method: "POST",
                body: { objectGroups: groups },
            }),
            invalidatesTags: ["Groups"],
        }),
        saveCustomProperties: builder.mutation<void, { projectId: string; data: CustomProperties }>({
            query: ({ projectId, data }) => ({
                url: `/explorer/${projectId}/scene-data`,
                method: "POST",
                body: { customProperties: data },
            }),
        }),
        saveJiraConfig: builder.mutation<
            void,
            { projectId: string; config: NonNullable<NonNullable<CustomProperties["integrations"]>["jira"]> }
        >({
            query: ({ projectId, config }) => ({
                url: `/explorer/${projectId}/jira-config`,
                method: "PUT",
                body: config,
            }),
        }),
        getDitioToken: builder.query<{ access_token: string; expires_in: number }, { projectId: string }>({
            query: ({ projectId }) => `/explorer/${projectId}/ditio`,
        }),
        saveDitioConfig: builder.mutation<
            { access_token?: string },
            { projectId: string; data: { client_id: string; client_secret: string } }
        >({
            query: ({ projectId, data }) => ({
                url: `/explorer/${projectId}/ditio`,
                method: "POST",
                body: data,
            }),
        }),
        getIntercomConfig: builder.query<{ appId: string; hash: string }, void>({
            query: () => `/explorer/intercom`,
            keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
        }),
    }),
});

function bookmarkScope(personal?: boolean) {
    return personal ? "personal" : "public";
}

/** A base query's result, typed as what the endpoint knows its response to be. */
async function typed<T, Meta = unknown>(
    result:
        | QueryReturnValue<unknown, FetchBaseQueryError, Meta>
        | PromiseLike<QueryReturnValue<unknown, FetchBaseQueryError, Meta>>,
): Promise<{ data: T; error?: undefined } | { error: FetchBaseQueryError; data?: undefined }> {
    const { data, error } = await result;
    return error ? { error } : { data: data as T };
}

export type BookmarkStateArgs = { projectId: string; bookmarkId: string; personal?: boolean };

/** Shared with the offline host-data capture, which fetches bookmark states outside RTK Query. */
export function bookmarkStatePath({ projectId, bookmarkId, personal }: BookmarkStateArgs) {
    return `/explorer/v3/${projectId}/bookmarks/${bookmarkScope(personal)}/${bookmarkId}`;
}

function bookmarksScopeTag({ projectId, personal }: { projectId: string; personal?: boolean }) {
    return { type: "Bookmarks", id: `${projectId}/${bookmarkScope(personal)}` } as const;
}

function idAndNameUniqueById(items: { id: number; name: string }[]) {
    const result: typeof items = [];
    for (const item of items) {
        if (!result.some((e) => e.id === item.id)) {
            result.push(item);
        }
    }
    return result;
}

export const {
    useGetOmega365ProjectConfigQuery,
    useSetOmega365ProjectConfigMutation,
    useLazyPreviewOmega365ProjectViewConfigQuery,
    useGetOmega365ViewDocumentLinksQuery,
    useGetOmega365AllViewDocumentLinksQuery,
    useLazyGetOmega365AllViewDocumentLinksQuery,
    useGetOmegaActivityTypesQuery,
    useGetOmegaOrgUnitsQuery,
    useCreateOmegaActivityMutation,
    useGetArcgisWidgetConfigQuery,
    usePutArcgisWidgetConfigMutation,
    useGetPropertyTreeFavoritesQuery,
    useSetPropertyTreeFavoritesMutation,
    useLazyGetProjectQuery,
    useGetProjectQuery,
    useLazyGetModelTreePermissionsQuery,
    useLazyGetDeviationProfilesQuery,
    useSetDeviationProfilesMutation,
    useCalcDeviationsMutation,
    useGetProjectProgressQuery,
    useLazyGetFileDownloadLinkQuery,
    useSearchEpsgQuery,
    useLazyCheckPermissionsQuery,
    useGetCurrentUserRoleAssignmentsQuery,
    useGetBookmarkCatalogQuery,
    useLazyGetBookmarkCatalogQuery,
    useGetBookmarkStateQuery,
    useLazyGetBookmarkStateQuery,
    useUpdateBookmarkGroupStatusMutation,
    useRenameBookmarkCollectionMutation,
    useCreateBookmarkMutation,
    useUpdateBookmarkMutation,
    useDeleteBookmarkMutation,
    useLazyGetSharelinkBookmarkQuery,
    useCreateSharelinkMutation,
    useLazyGetGroupIdsQuery,
    useSaveGroupsMutation,
    useSaveCustomPropertiesMutation,
    useSaveJiraConfigMutation,
    useLazyGetDitioTokenQuery,
    useSaveDitioConfigMutation,
    useGetIntercomConfigQuery,
    useGetOmegaObjectIdQuery,
    useGetProjectLatestVersionQuery,
} = dataV2Api;
